import type { ActiveFilter, FilterState } from '@bastion/list-view';
import {
  isTaskRating,
  type EpisodeDisplayPreferences,
  type SortConfig,
  type SubtaskCoverage,
  type ViewMode,
} from '../types';

const VALID_SORT_FIELDS = new Set<string>(['created_at', 'task_rating', 'frame_count']);
const VALID_SORT_ORDERS = new Set<string>(['asc', 'desc']);
const VALID_COVERAGE = new Set<string>(['full', 'partial', 'none']);
const VALID_VIEW_MODES = new Set<string>(['grid', 'table']);

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function stringValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return unique(value.filter((item): item is string => typeof item === 'string' && item.length > 0));
  }
  return typeof value === 'string' && value.length > 0 ? [value] : [];
}

function ratingValues(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return unique(
    raw
      .filter(isTaskRating)
      .map((n) => String(n)),
  );
}

function yesNoFromBooleans(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return unique(
    raw
      .filter((item): item is boolean => typeof item === 'boolean')
      .map((b) => (b ? 'yes' : 'no')),
  );
}

function upsert(
  base: FilterState,
  attributeId: string,
  operator: ActiveFilter['operator'],
  values: string[],
): FilterState {
  const rest = base.filters.filter((f) => f.attributeId !== attributeId);
  if (values.length === 0) {
    return { filters: rest };
  }
  return { filters: [...rest, { attributeId, operator, values }] };
}

export interface EpisodeFilterToolResult {
  filters: FilterState;
  display?: Partial<EpisodeDisplayPreferences>;
}

/**
 * Merge assistant tool-call args into the current episode list-view filter state.
 *
 * The assistant only sends a sparse subset of filters, so callers should
 * preserve existing values for omitted fields. Values are validated before
 * being applied — invalid values from the AI are silently ignored.
 *
 * Display fields (`view_mode`, `sort_by`, `sort_order`, `page_size`) are returned
 * in `display` when present so the caller can update `useEpisodeDisplayStore`.
 */
export function applyEpisodeFilterToolArgs(
  currentFilters: FilterState,
  currentDisplay: EpisodeDisplayPreferences,
  args: Record<string, unknown>,
): EpisodeFilterToolResult {
  let next: FilterState = currentFilters;
  const display: Partial<EpisodeDisplayPreferences> = {};

  if (args.search !== undefined) {
    const s = typeof args.search === 'string' ? args.search : '';
    next = upsert(next, 'search', 'is', s.trim().length > 0 ? [s] : []);
  }
  if (args.tag !== undefined) {
    next = upsert(next, 'tag', 'is', stringValues(args.tag));
  }
  if (args.source !== undefined) {
    next = upsert(next, 'source', 'is', stringValues(args.source));
  }
  if (args.rating !== undefined) {
    next = upsert(next, 'rating', 'is', ratingValues(args.rating));
  }
  if (args.hostname !== undefined) {
    next = upsert(next, 'hostname', 'is', stringValues(args.hostname));
  }
  if (args.hardware_version !== undefined) {
    next = upsert(next, 'hardware', 'is', stringValues(args.hardware_version));
  }
  if (args.collector_id !== undefined) {
    next = upsert(next, 'collector', 'is', stringValues(args.collector_id));
  }
  if (args.sop_id !== undefined) {
    next = upsert(next, 'sop', 'is', stringValues(args.sop_id));
  }

  // The assistant only emits absolute `date_from` / `date_to` (no preset
  // vocabulary). Persist them as the absolute form of the `created` chip
  // (2-value `[from, to]`). Existing values are only carried over when the
  // existing chip is also in absolute form.
  const existingCreated = currentFilters.filters.find((f) => f.attributeId === 'created');
  const existingIsAbsolute = (existingCreated?.values.length ?? 0) === 2;
  const existingFrom = existingIsAbsolute ? existingCreated?.values[0]?.trim() ?? '' : '';
  const existingTo = existingIsAbsolute ? existingCreated?.values[1]?.trim() ?? '' : '';

  if (args.date_from !== undefined || args.date_to !== undefined) {
    const from =
      args.date_from !== undefined && typeof args.date_from === 'string'
        ? args.date_from.trim()
        : existingFrom;
    const to =
      args.date_to !== undefined && typeof args.date_to === 'string'
        ? args.date_to.trim()
        : existingTo;
    next = upsert(next, 'created', 'is', from.length > 0 || to.length > 0 ? [from, to] : []);
  }

  if (args.reviewed !== undefined) {
    next = upsert(next, 'reviewed', 'is', yesNoFromBooleans(args.reviewed));
  }
  if (args.has_interventions !== undefined) {
    next = upsert(next, 'interventions', 'is', yesNoFromBooleans(args.has_interventions));
  }
  if (args.subtask_coverage !== undefined) {
    const cov = stringValues(args.subtask_coverage).filter((sc): sc is SubtaskCoverage =>
      VALID_COVERAGE.has(sc),
    );
    next = upsert(next, 'subtask_coverage', 'is', cov);
  }

  if (args.view_mode !== undefined) {
    const vm = args.view_mode as string;
    if (VALID_VIEW_MODES.has(vm)) {
      display.viewMode = vm as ViewMode;
    }
  }

  if (args.page_size !== undefined) {
    const n = typeof args.page_size === 'number' ? args.page_size : Number(args.page_size);
    if (Number.isFinite(n) && n > 0) {
      display.pageSize = Math.floor(n);
    }
  }

  if (args.sort_by !== undefined || args.sort_order !== undefined) {
    const baseSort = currentDisplay.sort;
    const field =
      typeof args.sort_by === 'string' && VALID_SORT_FIELDS.has(args.sort_by)
        ? (args.sort_by as SortConfig['field'])
        : baseSort.field;
    const order =
      typeof args.sort_order === 'string' && VALID_SORT_ORDERS.has(args.sort_order)
        ? (args.sort_order as SortConfig['order'])
        : baseSort.order;
    display.sort = { field, order };
  }

  const displayOut = Object.keys(display).length > 0 ? display : undefined;
  return { filters: next, display: displayOut };
}
